import { useState, useEffect, useMemo } from "react";
import {
    Grid,
    Typography,
    Avatar,
    Box,
    Chip,
    Container,
    Paper,
    TableCell,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TablePagination
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { fetchTraceabilityAsync } from "../../slices/traceabilitySlice.js";
import debounce from "lodash/debounce";
import { TableVirtuoso } from "react-virtuoso";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ImageLoading from "../alert/imageLoading.js";

const Traceability = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState(0);

    const { data: traceabilityData, status } = useSelector((state) => state.traceability);
    const customers = useSelector((state) => state.customers.data);
    const [startDate, setstartDate] = useState(null);
    const [finishDate, setfinishDate] = useState(null);
    const isLoading = status === "loading";
    const hasResults = traceabilityData.length > 0;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        setPage(0);
    }, [traceabilityData]);

    const paginatedData = useMemo(() => {
        return traceabilityData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [traceabilityData, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const uniqueCustomers = Array.from(new Set(customers.map(c => c.document)))
        .map(document => customers.find(c => c.document === document));

    useEffect(() => {
        dispatch(fetchTraceabilityAsync({ document: 0, start_date: "", end_date: "" }));
    }, [dispatch]);

    const debouncedSearch = useMemo(
        () => debounce((filtros) => {
            dispatch(fetchTraceabilityAsync(filtros));
        }, 600),
        [dispatch]
    );

    const aplyFilter = (customer, start, end) => {
        const params = {
            document: (customer === 0 || customer === "") ? 0 : customer,
            start_date: start ? start.format('YYYY-MM-DD') : "",
            end_date: end ? end.format('YYYY-MM-DD') : ""
        };
        debouncedSearch(params);
    };
    const handleStartChange = (newValue) => {
        setstartDate(newValue);
        aplyFilter(searchTerm, newValue, finishDate);
    };

    const handleFinishChange = (newValue) => {
        setfinishDate(newValue);
        aplyFilter(searchTerm, startDate, newValue);
    };


    const noCopyStyles = {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        cursor: 'default',
    };

    const handlePreventAction = (e) => e.preventDefault();

    const getStatusStyles = (statusName) => {
        const map = {
            "ACEPTADO": { bg: "#c8e6c9", color: "success.main" },
            "NO ACEPTADO": { bg: "#ffcdd2", color: "error.main" },
            "FALLECIDO": { bg: "#bdbdbd", color: "text.secondary" },
            "INTERESADO": { bg: "#fff3e0", color: "warning.main" },
            "PENDIENTE": { bg: "#f3e5f5", color: "primary.main" },
            "APROBADO": { bg: "#b5ffc1", color: "success.dark" },
        };
        return map[statusName] || { bg: "#ffebee", color: "error.main" };
    };

    return (
        <Container
            onCopy={handlePreventAction}
            onCut={handlePreventAction}
            onPaste={handlePreventAction}
            onContextMenu={handlePreventAction}
            maxWidth={false}
            sx={{ py: 4, ...noCopyStyles }}
        >
            <Box sx={{ flexGrow: 1, mb: 3 }}>
                <Grid container spacing={2} alignItems="flex-end" sx={{ boxShadow: 1, p: 2, borderRadius: 1 }}>

                    <Grid size={{ xs: 12, md: 5 }} item>
                        <FormControl fullWidth size="small">
                            <InputLabel id="documento-cliente">Documento de cliente</InputLabel>
                            <Select
                                labelId="documento-cliente"
                                label="Documento de cliente"
                                value={searchTerm}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSearchTerm(val);
                                    aplyFilter(val, startDate, finishDate);
                                }}
                            >
                                <MenuItem value={0}>Todos</MenuItem>
                                {uniqueCustomers.map((customer) => (
                                    <MenuItem key={customer.document} value={customer.document}>
                                        <Typography variant="body2">{customer.name} : {customer.document}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={7} alignContent={"center"}>
                        <Typography sx={{ mb: 1, ml: 0.5, color: 'text.secondary', textAlign: 'center' }}>
                            Créditos creados
                        </Typography>

                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Desde"
                                        value={startDate}
                                        onChange={handleStartChange}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Hasta"
                                        value={finishDate}
                                        onChange={handleFinishChange}
                                        minDate={startDate}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="medium"
                            onClick={() => {
                                setSearchTerm(0);
                                setstartDate(null);
                                setfinishDate(null);
                                aplyFilter(0, null, null);
                            }}
                            sx={{ height: '40px' }}
                        >
                            Limpiar
                        </Button>
                    </Grid>

                </Grid>
            </Box>
            {!hasResults && !isLoading ? (
                <Box sx={{ textAlign: "center" }}>
                    <ImageLoading />
                </Box>
            ) : (
                <Paper sx={{ height: { xs: 'auto', md: 600 }, minHeight: 400, borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <TableVirtuoso
                        data={paginatedData}
                        style={{ flexGrow: 1 }}
                        fixedHeaderContent={() => (
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Nombre cliente</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Documento</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Campaña</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Credito</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Ecargado</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Ciudad / Oficina</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Observacion</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Ultima gestion</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Fecha inicio</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Fecha movimiento</TableCell>
                            </TableRow>
                        )}
                        itemContent={(index, item) => {
                            const styles = getStatusStyles(item.type_data_name);
                            return (
                                <>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 250, fontWeight: 700 }}>
                                            <Avatar sx={{ bgcolor: "primary.light", width: 30, height: 30, fontSize: 12 }}>
                                                {item.customer_name?.charAt(0) || "?"}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600}>{item.customer_name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.type_data_name} size="small" sx={{ bgcolor: styles.bg, color: styles.color, fontWeight: 700 }} />
                                    </TableCell>
                                    <TableCell>{item.name_campaign}</TableCell>
                                    <TableCell>{item.name_credit}</TableCell>
                                    <TableCell>{item.name_user}</TableCell>
                                    <TableCell>{item.document}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.city_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{item.office_name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{item.observation}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{item.management_date}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{item.start_date?.slice(0, 10)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{item.date?.slice(0, 10)}</Typography>
                                    </TableCell>
                                </>
                            );
                        }}
                    />
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={traceabilityData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Filas por página"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </Paper>
            )}
        </Container>
    );
};

export default Traceability;